/***
 *
 *   ORGANIZATION
 *   Phase 8 — OrganizationSection cards + integration-card treatment (adapt,
 *   keep inline — no Integrations nav / routes).
 *
 **********/

import React, { useContext, useEffect, useRef } from 'react';
import {
    ViewContext,
    Form,
    Card,
    SettingsWrapper,
    Button,
    useNavigate,
    Animate,
    Link,
    EmptyState,
    Skeleton,
} from 'components/lib';
import { AuthContext } from 'app/auth';
import { useTranslate } from 'app/translations';
import { useApiContext } from 'app/api';
import { useQuery } from 'react-query';
import integrationsConfig from 'config/integrations';
import settings from '../../settings';
import Style from './organization.tailwind';

const { client_url_main } = settings;

export function Organization(props) {
    const { _t } = useTranslate();
    const navigate = useNavigate();
    const integrationsRef = useRef(null);
    const { postEvent, callApi } = useApiContext();
    const authContext = useContext(AuthContext);
    const viewContext = useContext(ViewContext);
    const accountId = authContext.user.account_id;

    const user = useQuery('user', () => callApi({ url: '/api/user' }));
    const account = user.data?.data.accounts.find((a) => a.id === accountId);
    const integrations = useQuery('integration', () => callApi({ url: '/api/integration' }));

    function closeAccount() {
        viewContext.modal.show(
            {
                title: _t('organization.close.title'),
                form: {},
                buttonText: _t('organization.close.label'),
                url: authContext.permission.owner ? '/api/account' : '/api/user',
                method: 'DELETE',
                destructive: true,
                text: _t('organization.delete.confirm'),
            },
            () => {
                postEvent('closed_account');
                localStorage.clear();
                navigate('/signup');
            },
        );
    }

    function createIntegration() {
        viewContext.modal.show(
            {
                title: _t('integration.add'),
                form: {
                    name: {
                        label: _t('integration.name'),
                        type: 'text',
                        required: true,
                    },
                    service: {
                        label: _t('integration.label'),
                        type: 'select',
                        required: true,
                        options: [
                            { value: 'clickup', label: 'Clickup' },
                            { value: 'slack', label: 'Slack' },
                        ],
                        default: 'clickup',
                    },
                },
                buttonText: _t('integration.connect'),
                url: '/api/integration/connect',
                method: 'post',
                headers: {
                    'Redirect-Url': window.location.href,
                },
            },
            (data, res) => {
                integrations.refetch();
                if (!res.redirect) return;
                window.location.href = res.redirect;
            },
        );
    }

    async function editIntegration(id) {
        const integration = integrations.data.data.find((x) => x.id === id);
        const integrationForm = await integrationsConfig[integration.service_name].form(
            integration,
        );

        viewContext.modal.show(
            {
                title: _t('integration.edit'),
                form: {
                    name: {
                        label: _t('integration.name'),
                        type: 'text',
                        required: true,
                        value: integration.name,
                    },
                    ...integrationForm,
                },
                buttonText: _t('save'),
                url: `/api/integration/${id}`,
                method: 'patch',
            },
            () => {
                integrations.refetch();
            },
        );
    }

    function deleteIntegration(id) {
        viewContext.modal.show(
            {
                title: _t('integration.delete.title'),
                form: {},
                buttonText: _t('delete'),
                url: `/api/integration/${id}`,
                method: 'delete',
                destructive: true,
                text: _t('integration.delete.confirm'),
            },
            () => {
                integrations.refetch();
            },
        );
    }

    useEffect(() => {
        if (window.location.hash === '#integrations' && integrations.isFetched) {
            setTimeout(() => {
                integrationsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 500);
        }
    }, [integrations]);

    const list = integrations?.data?.data || [];

    return (
        <SettingsWrapper>
            <Animate>
                <div className={Style.stack}>
                    <div>
                        <h1 className={Style.title}>{_t('organization.title')}</h1>
                        <p className={Style.description}>{_t('organization.updateInfo')}</p>
                    </div>

                    <Card
                        title={_t('organization.label')}
                        description={_t('organization.edit.text')}
                        loading={user.isLoading}>
                        {user?.data && account && (
                            <Form
                                buttonText={_t('save')}
                                url="/api/account"
                                method="PUT"
                                data={{
                                    logo: {
                                        label: 'Logo',
                                        type: 'logo',
                                        name: 'logo',
                                        required: false,
                                        placeholder: account.file_path,
                                    },
                                    domain: {
                                        label: _t('domain.label'),
                                        type: 'domain',
                                        required: true,
                                        value: account.domain,
                                        placeholder: `Ex: my-company-name`,
                                        domain: `.${client_url_main}`,
                                    },
                                    name: {
                                        type: 'text',
                                        label: _t('organization.name'),
                                        value: account.name,
                                    },
                                }}
                                callback={(res) => {
                                    if (authContext.user?.accounts?.length > 0) {
                                        const accounts = [...authContext.user.accounts];
                                        const userAccount =
                                            accounts[
                                                accounts.findIndex(
                                                    (x) => x.id === authContext.user.account_id,
                                                )
                                            ];
                                        userAccount.name = res.data.name;
                                        userAccount.domain = res.data.domain;
                                        userAccount.file_path = res.data.file_path;
                                        authContext.update({ accounts: [userAccount] });
                                        setTimeout(() => {
                                            if (res.data.domain !== account.domain) {
                                                authContext.switchDomain(res.data.domain);
                                            }
                                        }, 1);
                                    }
                                }}
                            />
                        )}
                        <div className={Style.viewLink}>
                            <Link url="/" text={_t('organization.viewPage')} />
                        </div>
                    </Card>

                    <div ref={integrationsRef} id="integrations">
                        <Card
                            title={_t('integration.title.multiple')}
                            description={_t('integration.connect')}
                            loading={integrations.isLoading}
                            footer={
                                <Button
                                    variant="accent"
                                    size="sm"
                                    icon="plus"
                                    text={_t('integration.add')}
                                    action={createIntegration}
                                />
                            }>
                            {integrations.isLoading ? (
                                <Skeleton className="h-24 w-full" />
                            ) : list.length === 0 ? (
                                <EmptyState
                                    bare
                                    icon="link"
                                    title={_t('integration.empty')}
                                    buttonText={_t('integration.add')}
                                    action={createIntegration}
                                />
                            ) : (
                                <ul className={Style.integrationList}>
                                    {list.map((integration) => (
                                        <li key={integration.id} className={Style.integrationCard}>
                                            <div className={Style.integrationInfo}>
                                                <img
                                                    src={`/assets/${integration.service_name}-logo.png`}
                                                    className={Style.integrationLogo}
                                                    alt={integration.service_name}
                                                />
                                                <div className="min-w-0">
                                                    <p className={Style.integrationName}>
                                                        {integration.name}
                                                    </p>
                                                    <p className={Style.integrationService}>
                                                        {integration.service_name}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className={Style.integrationContent}>
                                                {integrationsConfig[
                                                    integration.service_name
                                                ]?.item?.content?.(integration)}
                                            </div>
                                            <div className={Style.integrationButtons}>
                                                <Button
                                                    size="sm"
                                                    variant="secondary"
                                                    text={_t('edit')}
                                                    action={() => editIntegration(integration.id)}
                                                />
                                                <Button
                                                    size="sm"
                                                    variant="danger"
                                                    text={_t('delete')}
                                                    action={() => deleteIntegration(integration.id)}
                                                />
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </Card>
                    </div>

                    <Card
                        title={_t('organization.close.title')}
                        description={_t('organization.delete.confirm')}
                        className={Style.dangerCard}>
                        <Button
                            variant="danger"
                            size="sm"
                            text={_t('organization.close.label')}
                            action={closeAccount}
                        />
                    </Card>
                </div>
            </Animate>
        </SettingsWrapper>
    );
}
